import { useState, useCallback } from "react";
import MainServices from "../../../../../services/MainServices";
import { notifyWarn } from "../../../../../assets/elements/Toast";

export function useJobTitleData({
  currentPage,
  itemsPerPage,
  buildApiFilters,
  closeDeleteModal,
  openSuccessModal,
}) {
  const { getJobTitleData, changeJobTitleStatusData, bulkStatusUpdate } =
    MainServices();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [noData, setNoData] = useState(false);
  const [allSelected, setAllSelected] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const { filters: apiFilters, queryParams } = buildApiFilters();

    const details = {
      pageSize: itemsPerPage,
      pageNumber: currentPage,
      sorting: [
        { key: "status", direction: "asc" },
        { key: "createdAt", direction: "desc" },
      ],
      filters: apiFilters,
      ...queryParams,
    };

    try {
      const response = await getJobTitleData(details);
      if (response?.data) {
        const totalRecords = response.headers["x-coreplatform-total-records"];
        setTotalCount(totalRecords);

        const newData = response.data.data.doc;
        const updatedData = newData.map((row) => ({
          ...row,
          isSelected: false,
        }));

        setNoData(!totalRecords);

        const safeTotalRecords = Number(totalRecords) || 0;
        const safeItemsPerPage = Number(itemsPerPage) || 1;
        const calculatedTotalPages = Math.max(
          1,
          Math.ceil(safeTotalRecords / safeItemsPerPage)
        );

        setTotalPages(calculatedTotalPages);
        setData(updatedData);
      }
    } catch (error) {
      notifyWarn(
        error?.response?.data?.message || "An unexpected error occurred"
      );
      console.error("Error fetching job title data", error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, buildApiFilters, getJobTitleData]);

  const toggleSelectAll = useCallback(() => {
    const newAllSelected = !allSelected;
    setAllSelected(newAllSelected);
    setData((prevData) =>
      prevData.map((row) => ({
        ...row,
        isSelected: newAllSelected,
      }))
    );
  }, [allSelected]);

  const toggleRowSelection = useCallback((rowIndex) => {
    setData((prevData) => {
      const updatedData = prevData.map((row, index) =>
        index === rowIndex ? { ...row, isSelected: !row.isSelected } : row
      );

      const newAllSelected = updatedData.every((row) => row.isSelected);
      setAllSelected(newAllSelected);

      return updatedData;
    });
  }, []);

  const updateStatus = useCallback(
    async (row) => {
      const status = row.status === "inactive" ? "active" : "inactive";
      const details = { status };

      try {
        await changeJobTitleStatusData(
          details,
          row.publicId,
          row.concurrencyStamp
        );
        closeDeleteModal();
        openSuccessModal({
          title:
            row.status === "inactive"
              ? "Job Title Reactivated Successfully"
              : "Job Title Deactivated Successfully",
          message: "",
        });
        fetchData();
      } catch (error) {
        notifyWarn(
          error?.response?.data?.message || "An unexpected error occurred"
        );
        console.error("Error updating status", error);
      }
    },
    [changeJobTitleStatusData, fetchData]
  );

  const deleteRecord = useCallback(
    async (row) => {
      const details = { status: "delete" };

      try {
        await changeJobTitleStatusData(
          details,
          row.publicId,
          row.concurrencyStamp
        );
        closeDeleteModal();
        openSuccessModal({
          title: "Job Title Deleted Successfully",
        });
        fetchData();
      } catch (error) {
        notifyWarn(
          error?.response?.data?.message || "An unexpected error occurred"
        );
        console.error("Error deleting record", error);
      }
    },
    [changeJobTitleStatusData, fetchData]
  );

  return {
    data,
    loading,
    totalCount,
    totalPages,
    noData,
    allSelected,
    setAllSelected,
    fetchData,
    toggleSelectAll,
    toggleRowSelection,
    updateStatus,
    deleteRecord,
  };
}
